import { Brand } from "effect"
import { type Register, href } from "react-router"
import { RedirectTo } from "./RedirectTo.ts"

export type AppRoute = Brand.Branded<string, "Route">

export const AppRoute = <Path extends keyof Args>(location: Path, ...args: Args[Path]) =>
  Brand.nominal<AppRoute>()(href(location, ...args))

export const AppRouteWithSearchParams = <Path extends keyof Args>(
  location: Path,
  searchParams: URLSearchParams,
  ...args: Args[Path]
) => Brand.nominal<AppRoute>()(href(location, ...args) + `?${searchParams.toString()}`)

export const AppRedirectRoute =
  <Path extends keyof Args>(location: Path, ...args: Args[Path]) =>
  (redirectTo?: string) => {
    const searchParams =
      redirectTo === "/" || !redirectTo
        ? undefined
        : new URLSearchParams([[RedirectTo.Key, redirectTo]])

    return Brand.nominal<AppRoute>()(
      `${href(location, ...args)}${searchParams ? "?" + searchParams.toString() : ""}`,
    )
  }

export const AppRedirectRouteWithSearchParams =
  <Path extends keyof Args>(location: Path, searchParams: URLSearchParams, ...args: Args[Path]) =>
  (redirectTo?: string) => {
    const urlSearchPramas =
      redirectTo === "/" || !redirectTo
        ? searchParams
        : new URLSearchParams([...searchParams.entries(), [RedirectTo.Key, redirectTo]])

    return Brand.nominal<AppRoute>()(
      `${href(location, ...args)}${urlSearchPramas ? "?" + urlSearchPramas.toString() : ""}`,
    )
  }

type AnyParams = Record<string, string | undefined>
type AnyPages = Record<string, { params: AnyParams }>
type Pages = Register extends {
  pages: infer RegisteredPages extends AnyPages
}
  ? RegisteredPages
  : AnyPages
type Params = {
  [K in keyof Pages]: Pages[K]["params"]
}
type Args = {
  [K in keyof Params]: ToArgs<Params[K]>
}
type ToArgs<T> = Equal<T, {}> extends true ? [] : Partial<T> extends T ? [T] | [] : [T]
type Equal<X, Y> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? true : false
